'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import Icon from '@/components/ui/AppIcon';
import { login, getSessionUser, clearSession, changePassword, LoginChallenge, getUserContexts, switchContext } from '@/lib/api-client';
import RoleSelectionModal from '@/components/common/RoleSelectionModal';

interface FormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface FormErrors {
  email?: string;
  password?: string;
  general?: string;
}

const LoginForm = () => {
  const router = useRouter();
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Password change challenge state
  const [needsPasswordChange, setNeedsPasswordChange] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [challengeSession, setChallengeSession] = useState<string | null>(null);
  const [givenName, setGivenName] = useState('');
  const [familyName, setFamilyName] = useState('');

  // Role selection state
  const [showRoleSelection, setShowRoleSelection] = useState(false);
  const [availableRoles, setAvailableRoles] = useState<any[]>([]);
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    setIsHydrated(true);

    // Check if user is already logged in
    const user = getSessionUser();
    if (user) {
      // User is logged in, redirect to their dashboard instead of clearing session
      if (user.user_type === 'ROOT') {
        router.push('/platform-dashboard');
      } else if (user.user_type === 'TENANT') {
        router.push('/tenant-dashboard');
      } else if (user.user_type === 'CUSTOMER') {
        router.push('/customer-dashboard');
      } else if (user.user_type === 'AGENT' || user.user_type === 'AGENT_USER') {
        router.push('/agent-dashboard');
      } else {
        router.push('/dashboard');
      }
    }
    // Only clear session if no user is logged in
    // This prevents clearing session during navigation redirects
  }, [router]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleLogin = async (email: string, password: string, rememberMe: boolean = true) => {
    setIsLoading(true);
    setErrors({});

    try {
      const result = await login(email, password, rememberMe);

      // Check if this is a password change challenge
      if ('challengeName' in result && result.challengeName === 'NEW_PASSWORD_REQUIRED') {
        setNeedsPasswordChange(true);
        setChallengeSession(result.session);
        setIsLoading(false);
        return;
      }

      // Check if user has multiple roles
      const contextsData = await getUserContexts();
      if (contextsData.contexts && contextsData.contexts.length > 1) {
        // User has multiple roles - show selection modal
        setUserEmail(email);
        setAvailableRoles(contextsData.contexts);
        setShowRoleSelection(true);
        setIsLoading(false);
        return;
      }

      // Single role - redirect based on user type
      // Type guard: result is the success response with user property
      if ('user' in result) {
        redirectToUserDashboard(result.user.user_type);
      }

      // Note: We don't set isLoading to false here because the page is redirecting
      // The loading state will be cleared when the new page loads
    } catch (error: any) {
      console.error('Login error:', error);

      setErrors({
        general: error instanceof Error ? error.message : 'Login failed. Please try again.',
      });
      setIsLoading(false);
    }
  };

  const redirectToUserDashboard = (userType: string) => {
    if (userType === 'ROOT') {
      router.push('/platform-dashboard');
    } else if (userType === 'TENANT') {
      router.push('/tenant-dashboard');
    } else if (userType === 'AGENT' || userType === 'AGENT_USER') {
      // AGENT_USER is deprecated, but check for backwards compatibility
      router.push('/agent-dashboard');
    } else if (userType === 'CUSTOMER') {
      router.push('/customer-dashboard');
    } else {
      // Fallback to dashboard redirect page
      router.push('/dashboard');
    }
  };

  const handleRoleSelection = async (selectedUserType: string) => {
    try {
      setIsLoading(true);

      // Switch to the selected role context
      await switchContext(selectedUserType);

      // Redirect to appropriate dashboard
      redirectToUserDashboard(selectedUserType);
    } catch (error: any) {
      console.error('Role switch error:', error);
      setErrors({
        general: 'Failed to switch role. Please try again.',
      });
      setShowRoleSelection(false);
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    // Validate passwords
    if (!newPassword || newPassword.length < 8) {
      setErrors({ general: 'Password must be at least 8 characters long' });
      setIsLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrors({ general: 'Passwords do not match' });
      setIsLoading(false);
      return;
    }

    if (!challengeSession) {
      setErrors({ general: 'Session expired. Please try logging in again.' });
      setIsLoading(false);
      return;
    }

    try {
      const result = await changePassword(formData.email, newPassword, challengeSession, givenName, familyName);

      // Password changed successfully
      setNeedsPasswordChange(false);
      setChallengeSession(null);
      setNewPassword('');
      setConfirmPassword('');
      setGivenName('');
      setFamilyName('');

      // If auto-login succeeded, redirect based on user type
      if (result.user) {
        if (result.user.user_type === 'ROOT') {
          router.push('/platform-dashboard');
        } else if (result.user.user_type === 'TENANT') {
          router.push('/tenant-dashboard');
        } else if (result.user.user_type === 'AGENT') {
          router.push('/agent-dashboard');
        } else {
          router.push('/customer-dashboard');
        }
      } else {
        // Auto-login failed, try manual login
        await handleLogin(formData.email, newPassword);
      }
    } catch (error) {
      setErrors({
        general: error instanceof Error ? error.message : 'Failed to change password',
      });
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    await handleLogin(formData.email, formData.password, formData.rememberMe);
  };

  if (!isHydrated) {
    return (
      <div className="w-full max-w-md bg-surface rounded-lg shadow-lg p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-12 bg-muted rounded" />
          <div className="space-y-4">
            <div className="h-10 bg-muted rounded" />
            <div className="h-10 bg-muted rounded" />
          </div>
          <div className="h-12 bg-muted rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md bg-surface rounded-lg shadow-lg p-8">
      {/* Logo and Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center mb-4">
          <Image
            src="/assets/images/Luminai-Logo-light.png"
            alt="Luminai"
            width={200}
            height={56}
            className="h-14 w-auto"
            priority
          />
        </div>
        <h1 className="text-2xl font-semibold text-text-primary mb-2">Welcome</h1>
        <p className="text-sm text-text-secondary">Sign in to manage your agent deployments</p>
      </div>

      {/* Error Message */}
      {errors.general && (
        <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-md flex items-start gap-3">
          <Icon name="ExclamationCircleIcon" size={20} className="text-error flex-shrink-0 mt-0.5" />
          <p className="text-sm text-error">{errors.general}</p>
        </div>
      )}

      {/* Password Change Form (shown when temp password was used) */}
      {needsPasswordChange ? (
        <form onSubmit={handlePasswordChange} className="space-y-6">
          <div className="mb-4 p-4 bg-primary/10 border border-primary/20 rounded-md">
            <p className="text-sm text-text-primary">
              <strong>Password Change Required</strong>
            </p>
            <p className="text-xs text-text-secondary mt-1">
              This is your first login. Please set a new password and enter your name.
            </p>
          </div>

          {/* First Name Field */}
          <div>
            <label htmlFor="givenName" className="block text-sm font-medium text-text-primary mb-2">
              First Name
            </label>
            <input
              type="text"
              id="givenName"
              value={givenName}
              onChange={(e) => setGivenName(e.target.value)}
              className="w-full px-3 py-3 bg-background border border-border rounded-md text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary transition-smooth"
              placeholder="Enter your first name"
              disabled={isLoading}
              required
            />
          </div>

          {/* Last Name Field */}
          <div>
            <label htmlFor="familyName" className="block text-sm font-medium text-text-primary mb-2">
              Last Name
            </label>
            <input
              type="text"
              id="familyName"
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
              className="w-full px-3 py-3 bg-background border border-border rounded-md text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary transition-smooth"
              placeholder="Enter your last name"
              disabled={isLoading}
              required
            />
          </div>

          {/* New Password Field */}
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-text-primary mb-2">
              New Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Icon name="LockClosedIcon" size={20} className="text-text-secondary" />
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full pl-10 pr-12 py-3 bg-background border border-border rounded-md text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary transition-smooth"
                placeholder="Enter new password"
                disabled={isLoading}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                disabled={isLoading}
              >
                <Icon
                  name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'}
                  size={20}
                  className="text-text-secondary hover:text-text-primary transition-smooth"
                />
              </button>
            </div>
            <p className="mt-1 text-xs text-text-secondary">
              Must be at least 8 characters with uppercase, lowercase, number, and special character
            </p>
          </div>

          {/* Confirm Password Field */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-text-primary mb-2">
              Confirm Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Icon name="LockClosedIcon" size={20} className="text-text-secondary" />
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-background border border-border rounded-md text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary transition-smooth"
                placeholder="Confirm new password"
                disabled={isLoading}
                required
              />
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 px-4 bg-primary text-primary-foreground rounded-md font-medium hover:bg-primary/90 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                <span>Changing Password...</span>
              </>
            ) : (
              <span>Change Password</span>
            )}
          </button>
        </form>
      ) : (
        /* Login Form */
        <form onSubmit={handleSubmit} className="space-y-6">
        {/* Email Field */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-2">
            Email Address
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon name="EnvelopeIcon" size={20} className="text-text-secondary" />
            </div>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`w-full pl-10 pr-4 py-3 bg-background border rounded-md text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary transition-smooth ${
                errors.email ? 'border-error' : 'border-border'
              }`}
              placeholder="you@example.com"
              disabled={isLoading}
            />
          </div>
          {errors.email && (
            <p className="mt-2 text-sm text-error flex items-center gap-1">
              <Icon name="ExclamationCircleIcon" size={16} />
              {errors.email}
            </p>
          )}
        </div>

        {/* Password Field */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-text-primary mb-2">
            Password
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon name="LockClosedIcon" size={20} className="text-text-secondary" />
            </div>
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className={`w-full pl-10 pr-12 py-3 bg-background border rounded-md text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary transition-smooth ${
                errors.password ? 'border-error' : 'border-border'
              }`}
              placeholder="Enter your password"
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              disabled={isLoading}
            >
              <Icon
                name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'}
                size={20}
                className="text-text-secondary hover:text-text-primary transition-smooth"
              />
            </button>
          </div>
          {errors.password && (
            <p className="mt-2 text-sm text-error flex items-center gap-1">
              <Icon name="ExclamationCircleIcon" size={16} />
              {errors.password}
            </p>
          )}
        </div>

        {/* Remember Me & Forgot Password */}
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="rememberMe"
              checked={formData.rememberMe}
              onChange={handleInputChange}
              className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-2 focus:ring-primary cursor-pointer"
              disabled={isLoading}
            />
            <span className="text-sm text-text-secondary">Remember me</span>
          </label>
          <Link
            href="/forgot-password"
            className="text-sm text-primary hover:text-primary/80 transition-smooth"
          >
            Forgot password?
          </Link>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3 bg-primary text-primary-foreground rounded-md font-medium hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
              <span>Signing in...</span>
            </>
          ) : (
            <>
              <Icon name="ArrowRightOnRectangleIcon" size={20} />
              <span>Sign In</span>
            </>
          )}
        </button>
      </form>
      )}

      {/* Footer */}
      <div className="mt-8 text-center">
        <p className="text-xs text-text-secondary">
          By signing in, you agree to our{' '}
          <Link href="/login" className="text-primary hover:text-primary/80 transition-smooth">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link href="/login" className="text-primary hover:text-primary/80 transition-smooth">
            Privacy Policy
          </Link>
        </p>
      </div>

      {/* Role Selection Modal */}
      <RoleSelectionModal
        isOpen={showRoleSelection}
        roles={availableRoles}
        userEmail={userEmail}
        onSelectRole={handleRoleSelection}
      />
    </div>
  );
};

export default LoginForm;
